import { apiPost } from "./apiClient";

export const DROPDOWN_SETS = [
  { mode: "engine_layout", label: "Engine layout", group: 'Operations' },
  { mode: "transmission_type", label: "Transmission type", group: 'Operations' },
  { mode: "drivetrain", label: "Drivetrain", group: 'Operations' },
  { mode: "part_side", label: "Part side", group: 'Operations' },
  { mode: "part_condition_origin", label: "Part condition", group: 'Operations' },
  { mode: 'address_type', label: 'Address type', group: 'Customers' },
  { mode: 'emirate', label: 'Emirate', group: 'Customers' },
  { mode: 'contact_method', label: 'Contact method', group: 'Operations' },
  { mode: 'cash_collection_location', label: 'Cash collection place', group: 'Payments' },
  ...[
    ['vat_mode', 'VAT mode'], ['settlement_arrangement', 'Settlement arrangement'],
    ['payment_method_expected', 'Expected payment method'], ['payment_due_trigger', 'When due'],
    ['cheque_state', 'Cheque state'], ['payment_link_provider', 'Payment link provider'],
    ['settlement_change_action', 'Settlement change'], ['cod_revision_adjustment', 'COD balance on revision'],
    ['accounts_cheque_action', 'Cheque outcome'], ['accounts_cheque_resolution', 'Cheque resolution'],
    ['accounts_transfer_decision', 'Transfer decision'], ['accounts_settlement_decision', 'Settlement decision'],
    ['accounts_provider_outcome', 'Provider outcome'], ['accounts_cash_handoff_decision', 'Cash handoff decision'],
    ['accounts_pos_decision', 'Card (POS) decision'],
  ].map(([mode, label]) => ({ mode, label, group: 'Payments', locked: true })),
  ...[
    ['cancellation_scope', 'Cancellation coverage'], ['cancellation_reason', 'Cancellation reason'],
    ['logistics_task_type', 'Logistics task type'], ['logistics_method', 'Logistics method'],
    ['logistics_stop_type', 'Stop type'], ['field_outcome', 'Completed work'], ['custody_location_type', 'Custody / storage place'],
  ].map(([mode, label]) => ({ mode, label, group: 'Operations', locked: true })),
  ...[
    ['warranty_case_type', 'Warranty problem'], ['warranty_supplier_response', 'Supplier response'],
    ['warranty_customer_solution', 'Customer solution'], ['change_decision', 'Change decision'],
    ['change_resolution', 'Change outcome'], ['residual_condition', 'Returned stock condition'],
    ['residual_final_outcome', 'Returned stock final outcome'],
  ].map(([mode, label]) => ({ mode, label, group: 'Warranty', locked: true })),
];
export const DROPDOWN_VALUES_CHANGED = "uzdpart360:dropdown-values-changed";
export const isOptionActive = row => row.status == null || row.status === true || row.status === 1;
export function normalizeDropdownRows(raw) {
  if (!Array.isArray(raw)) throw new Error("Expected an option array. Existing settings were not changed.");
  const ids = new Set();
  let next = Math.max(0, ...raw.map(row => Number.isInteger(row?.id) ? row.id : 0)) + 1;
  return raw.map(row => {
    if (!row || row.value == null || typeof row.label !== "string") throw new Error("Invalid dropdown entry. Existing settings were not changed.");
    const id = row.id == null ? next++ : row.id;
    if (!Number.isSafeInteger(id) || id < 1 || ids.has(id)) throw new Error("Dropdown IDs must be unique positive integers.");
    ids.add(id);
    return { ...row, id, status: isOptionActive(row) ? 1 : 0 };
  });
}
export function dropdownOptions(rows, selected, allowed) {
  const chosen = value => selected != null && selected !== '' && (Array.isArray(selected) ? selected : [selected]).some(item => String(item) === String(value));
  const ordered = allowed ? allowed.map(value => rows.find(row => String(row.value) === String(value))).filter(Boolean) : rows;
  const included = new Set(ordered.map(row => String(row.value)));
  return [...ordered, ...rows.filter(row => !included.has(String(row.value)) && chosen(row.value))]
    .filter(row => isOptionActive(row) || chosen(row.value)).map(row => ({
    value: row.value, label: row.label, disabled: !isOptionActive(row) || Boolean(allowed && !allowed.some(value => String(value) === String(row.value))),
  }));
}
export function validateDropdownRows(rows) {
  const normalized = normalizeDropdownRows(rows);
  const values = new Set();
  for (const row of normalized) {
    if (!String(row.value).trim() || !row.label.trim()) throw new Error("Every option needs a value and label.");
    if (values.has(String(row.value))) throw new Error("Option values must be unique, including disabled entries.");
    values.add(String(row.value));
  }
  return normalized;
}
// Semantic fingerprint: property order is immaterial; array order is intentional.
export function dropdownFingerprint(value) {
  if (Array.isArray(value)) return `[${value.map(dropdownFingerprint).join(",")}]`;
  if (value && typeof value === "object") return `{${Object.keys(value).sort().map(key => `${JSON.stringify(key)}:${dropdownFingerprint(value[key])}`).join(",")}}`;
  return JSON.stringify(value);
}
export async function fetchDropdownValues(mode) {
  const response = await apiPost("/whatsapp/get_misc_mode", { mode });
  if (!response.data?.success) throw new Error(response.data?.message || "Unable to load dropdown values.");
  const raw = response.data.data;
  return { raw, rows: normalizeDropdownRows(raw) };
}
export async function saveDropdownValues(mode, rows, baseline) {
  const normalized = validateDropdownRows(rows);
  validateLockedDropdown(mode, normalized, baseline);
  const latest = await fetchDropdownValues(mode);
  if (dropdownFingerprint(latest.raw) !== dropdownFingerprint(baseline)) throw new Error("These values changed elsewhere. Reload and review before saving.");
  const response = await apiPost("/whatsapp/save_misc_mode", { mode, moredetails: normalized });
  if (!response.data?.success) throw new Error(response.data?.message || "Unable to save dropdown values.");
  window.dispatchEvent(new CustomEvent(DROPDOWN_VALUES_CHANGED, { detail: { mode } }));
  return { raw: normalized, rows: normalized };
}

export function validateLockedDropdown(mode, rows, baseline) {
  if (!DROPDOWN_SETS.find(set => set.mode === mode)?.locked) return;
  const original = normalizeDropdownRows(baseline);
  if (rows.length !== original.length || rows.some(row => !original.some(saved => saved.id === row.id && saved.value === row.value))) {
    throw new Error('Only labels, order and visibility can change for this list.');
  }
}

export async function fetchMiscOptionRows(mode) {
  const response = await apiPost('/whatsapp/get_misc_mode', { mode });
  if (response.data?.success === false) throw new Error("Couldn't load options");
  const data = response.data?.data;
  const rows = mode === 'customer_type' ? (Array.isArray(data) ? data : data?.types) : data;
  if (!Array.isArray(rows) || !rows.length) throw new Error("Couldn't load options");
  return validateDropdownRows(rows);
}
